import { useQuery } from '@apollo/client';
import { Card, Modal, Spin, Table, Typography } from 'antd';
import { useGetLeaderboardHistoryQuery } from 'gql/types';
import { FC, useCallback, useMemo, useState } from 'react';

import { GET_RECRUITER_DONORS_QUERY } from '../../graphql/queries';
import { useRecruiterDashboardSettings } from '../../hooks/useRecruiterDashboardSettings';
import {
  useRecruiterLeaderboard,
  LeaderboardPeriod,
  IRecruiterLeaderboardEntry,
} from '../../hooks/useRecruiterLeaderboard';
import {
  RecruitersLeaderboardCard,
  ILeaderboardHistoryWeek,
} from '../cards/RecruitersLeaderboardCard';

const { Text } = Typography;

const fmtDate = (d: string | Date): string => {
  const dt = typeof d === 'string' ? new Date(d) : d;
  return dt.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

interface IDonorRow {
  donorId: string;
  donorName: string;
  ownerFullName?: string;
  status: string;
  location?: string;
  region?: string;
  sglDbl?: string;
  registeredAt?: string;
  approvedDonationML?: number;
  weight?: number;
}

const RecruiterLeaderboardTab: FC = () => {
  const { settings } = useRecruiterDashboardSettings();
  const [leaderboardPeriod, setLeaderboardPeriod] =
    useState<LeaderboardPeriod>('today');
  const { entries: leaderboardEntries, loading: leaderboardLoading } =
    useRecruiterLeaderboard(leaderboardPeriod);

  // History query
  const { data: historyData, loading: historyLoading } =
    useGetLeaderboardHistoryQuery({
      variables: { input: { weeks: 52 } },
      skip: leaderboardPeriod !== 'history',
    });

  const historyWeeks: ILeaderboardHistoryWeek[] = useMemo(
    () =>
      (historyData?.getLeaderboardHistory?.weeks ?? []).map((w) => ({
        weekNumber: w.weekNumber,
        year: w.year,
        weekEndDate: w.weekEndDate,
        winnerName: w.winnerName ?? undefined,
        winnerRegion: w.winnerRegion ?? undefined,
        winnerUserId: w.winnerUserId ?? undefined,
        recruitedCount: w.recruitedCount,
      })),
    [historyData]
  );

  // Modal state
  const [modalEntry, setModalEntry] =
    useState<IRecruiterLeaderboardEntry | null>(null);

  const dateBounds = useMemo(() => {
    const now = new Date();
    if (leaderboardPeriod === 'today') {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return { start, end: now };
    }
    // thisWeek — Monday to now
    const day = now.getDay();
    const diff = day === 0 ? 6 : day - 1; // Monday = 0 offset
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() - diff
    );
    return { start, end: now };
  }, [leaderboardPeriod]);

  // Fetch recruiter donors when modal is open
  const {
    data: donorData,
    loading: donorLoading,
    error: donorError,
  } = useQuery(GET_RECRUITER_DONORS_QUERY, {
    variables: {
      input: {
        recruiterId: modalEntry?.userId || '',
        start: dateBounds.start.toISOString(),
        end: dateBounds.end.toISOString(),
      },
    },
    skip: !modalEntry,
    fetchPolicy: 'network-only',
  });

  const donors: IDonorRow[] = useMemo(
    () => donorData?.getRecruiterDonors?.donors ?? [],
    [donorData]
  );

  const handleEntryClick = useCallback((entry: IRecruiterLeaderboardEntry) => {
    setModalEntry(entry);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalEntry(null);
  }, []);

  return (
    <div style={{ padding: '16px 0' }}>
      <Card
        style={{ padding: 0, overflow: 'hidden', maxWidth: 500 }}
        bodyStyle={{ padding: 0 }}
        bordered={false}
      >
        <RecruitersLeaderboardCard
          period={leaderboardPeriod}
          onPeriodChange={setLeaderboardPeriod}
          entries={leaderboardEntries}
          loading={leaderboardLoading}
          settings={settings}
          onEntryClick={handleEntryClick}
          historyWeeks={historyWeeks}
          historyLoading={historyLoading}
        />
      </Card>

      <Modal
        visible={!!modalEntry}
        onCancel={handleModalClose}
        footer={null}
        width={900}
        title={
          modalEntry ? (
            <Text strong>
              {modalEntry.firstName} {modalEntry.lastName} — Recruited Donors (
              {leaderboardPeriod === 'today' ? 'Today' : 'This Week'})
            </Text>
          ) : null
        }
        destroyOnClose
      >
        {donorError && (
          <div
            style={{
              padding: 12,
              background: '#fff2f0',
              borderRadius: 4,
              marginBottom: 8,
            }}
          >
            <Text type="danger" style={{ fontSize: 12 }}>
              Error: {donorError.message}
            </Text>
          </div>
        )}
        {donorLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <Table
            rowKey="donorId"
            dataSource={donors}
            size="small"
            scroll={{ x: 700 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} donors`,
            }}
            columns={[
              {
                title: 'Registered',
                dataIndex: 'registeredAt',
                width: 100,
                defaultSortOrder: 'descend',
                sorter: (a: IDonorRow, b: IDonorRow) =>
                  new Date(a.registeredAt || 0).getTime() -
                  new Date(b.registeredAt || 0).getTime(),
                render: (v: string) => (v ? fmtDate(v) : '—'),
              },
              { title: 'Donor', dataIndex: 'donorName', width: 120 },
              { title: 'Owner', dataIndex: 'ownerFullName', width: 120 },
              { title: 'Status', dataIndex: 'status', width: 80 },
              { title: 'Location', dataIndex: 'location', width: 120 },
              { title: 'Region', dataIndex: 'region', width: 100 },
              { title: 'SGL/DBL', dataIndex: 'sglDbl', width: 70 },
              {
                title: 'Approved mL',
                dataIndex: 'approvedDonationML',
                width: 90,
                render: (v: number) => (v ? `${v} mL` : '—'),
              },
              {
                title: 'Weight',
                dataIndex: 'weight',
                width: 80,
                render: (v: number) => (v ? `${v} kg` : '—'),
              },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={9}>
                  <strong>Total Donors: {donors.length}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        )}
      </Modal>
    </div>
  );
};

export default RecruiterLeaderboardTab;
